"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  AudioLines,
  Check,
  Lightbulb,
  PhoneOff,
  Target,
  X,
} from "lucide-react";

export type HeroVisualStrings = {
  program: string;
  persona_name: string;
  persona_role: string;
  call_type: string;
  live_label: string;
  end_call: string;
  score_label: string;
  score_delta: string;
  stage2: string;
  stage3: string;
  stage5: string;
  coach_label: string;
  coach_issue: string;
  coach_heading: string;
  coach_prompt1: string;
  coach_prompt2: string;
  coach_takeaway: string;
  loop_roleplay: string;
  loop_score: string;
  loop_coach: string;
};

const STEP_DURATION = 3800;

export default function LandingHeroVisual({
  strings,
}: {
  strings: HeroVisualStrings;
}) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) return;

    const timer = window.setTimeout(() => {
      setActive((current) => (current + 1) % 3);
    }, STEP_DURATION);

    return () => window.clearTimeout(timer);
  }, [active]);

  const steps = [
    strings.loop_roleplay,
    strings.loop_score,
    strings.loop_coach,
  ];
  const scores = [
    { label: strings.stage2, value: 91 },
    { label: strings.stage3, value: 74 },
    { label: strings.stage5, value: 67 },
  ];

  return (
    <div className="relative mx-auto w-full max-w-[620px] lg:mx-0">
      <div className="mb-5 flex items-center justify-center px-2 sm:mb-6">
        {steps.map((step, index) => (
          <div key={step} className="flex items-center">
            <button
              type="button"
              onClick={() => setActive(index)}
              className={`relative rounded-sm px-2 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#26734a] focus-visible:ring-offset-2 sm:px-4 sm:text-sm ${
                active === index
                  ? "text-[#0d1711]"
                  : "text-[#9ba39d] hover:text-[#536159]"
              }`}
              aria-pressed={active === index}
            >
              {step}
              <span
                className={`absolute inset-x-2 -bottom-0.5 h-0.5 origin-left bg-[#8de541] transition-transform duration-500 sm:inset-x-4 ${
                  active === index ? "scale-x-100" : "scale-x-0"
                }`}
              />
            </button>
            {index < steps.length - 1 && (
              <span className="h-px w-6 bg-[#d5dbd4] sm:w-10" />
            )}
          </div>
        ))}
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute -inset-x-6 bottom-1 h-40 bg-[#a8ef5a]/28 blur-[58px]" />
        <div className="pointer-events-none absolute -bottom-3 -right-3 h-[94%] w-[94%] rounded-[30px] bg-[#a8ef5a]" />

        <div className="relative min-h-[455px] overflow-hidden rounded-[28px] border border-[#e0e5df] bg-white shadow-[0_26px_80px_rgba(42,61,48,0.14)] sm:min-h-[470px]">
          <div
            key={active}
            className="min-h-[455px] animate-in fade-in slide-in-from-bottom-2 duration-500 sm:min-h-[470px]"
          >
            {active === 0 && (
              <div className="flex min-h-[455px] flex-col px-6 py-6 text-center sm:min-h-[470px] sm:px-10 sm:py-7">
                <div className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#7b857e]">
                  <span>{strings.program}</span>
                </div>

                <div className="my-auto py-4">
                  <div className="relative mx-auto w-fit">
                    <span className="absolute -inset-4 rounded-full bg-[#a8ef5a]/18 blur-xl" />
                    <span className="absolute -inset-2 animate-pulse rounded-full border-2 border-[#a8ef5a]/55" />
                    <Image
                      src="/landing/prospect_ia_realiste_femme.png"
                      alt=""
                      width={170}
                      height={170}
                      priority
                      className="relative h-[142px] w-[142px] rounded-full border-[6px] border-white object-cover shadow-[0_0_0_3px_#a8ef5a,0_18px_45px_rgba(38,115,74,0.2)] sm:h-[160px] sm:w-[160px]"
                    />
                    <span className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-[#0d1711] px-3 py-1.5 text-[10px] font-semibold text-white shadow-lg">
                      <AudioLines className="h-3.5 w-3.5 text-[#a8ef5a]" />
                      {strings.live_label}
                    </span>
                  </div>

                  <h3 className="mt-7 text-2xl font-semibold tracking-[-0.035em] text-[#0d1711] sm:text-[1.75rem]">
                    {strings.persona_name}
                  </h3>
                  <p className="mt-1 text-sm text-[#69746d]">
                    {strings.persona_role}
                  </p>

                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#eef2ed] px-3 py-1.5 text-xs font-medium text-[#59645d]">
                      <Target className="h-3.5 w-3.5" />
                      {strings.call_type}
                    </span>
                    <span className="rounded-full bg-[#eef2ed] px-3 py-1.5 text-xs font-medium text-[#59645d]">
                      08:42
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <div className="flex h-10 items-center gap-1 px-3">
                    {[12, 22, 15, 30, 19, 34, 16, 27, 13].map(
                      (height, index) => (
                        <span
                          key={index}
                          className="w-1 rounded-full bg-[#26734a]"
                          style={{ height }}
                        />
                      )
                    )}
                  </div>
                  <span className="flex h-11 items-center gap-2 rounded-full bg-[#ef5149] px-5 text-sm font-semibold text-white shadow-sm">
                    <PhoneOff className="h-4 w-4" />
                    {strings.end_call}
                  </span>
                </div>
              </div>
            )}

            {active === 1 && (
              <div className="flex min-h-[455px] flex-col px-6 py-6 sm:min-h-[470px] sm:px-10 sm:py-8">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#7b857e]">
                    {strings.score_label}
                  </p>
                  <span className="rounded-full bg-[#e7f6dc] px-3 py-1 text-xs font-semibold text-[#26734a]">
                    {strings.score_delta}
                  </span>
                </div>

                <div className="mx-auto mt-5 flex h-36 w-36 items-center justify-center rounded-full bg-[#f2f8ed] shadow-[0_0_0_10px_#f7faf5] ring-4 ring-[#a8ef5a]">
                  <div className="text-center">
                    <span className="text-5xl font-semibold tracking-[-0.07em] text-[#0d1711]">
                      82
                    </span>
                    <span className="block text-xs font-medium text-[#7b857e]">
                      / 100
                    </span>
                  </div>
                </div>

                <div className="mt-7 space-y-3">
                  {scores.map((score) => (
                    <div
                      key={score.label}
                      className="grid grid-cols-[1fr_auto] items-center gap-4 rounded-xl border border-[#e5e9e3] px-4 py-3"
                    >
                      <div>
                        <div className="mb-2 flex items-center gap-2">
                          <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full ${
                              score.value >= 75
                                ? "bg-[#dff5ce] text-[#26734a]"
                                : "bg-[#fff0dd] text-[#b47419]"
                            }`}
                          >
                            {score.value >= 75 ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <ArrowRight className="h-3 w-3" />
                            )}
                          </span>
                          <span className="text-sm font-medium text-[#334038]">
                            {score.label}
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-[#edf0eb]">
                          <div
                            className={`h-full rounded-full ${
                              score.value >= 75
                                ? "bg-[#26734a]"
                                : "bg-[#d69d3f]"
                            }`}
                            style={{ width: `${score.value}%` }}
                          />
                        </div>
                      </div>
                      <span className="font-mono text-sm font-semibold text-[#0d1711]">
                        {score.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {active === 2 && (
              <div className="flex min-h-[455px] flex-col px-6 py-6 sm:min-h-[470px] sm:px-10 sm:py-8">
                <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#26734a]">
                  <Lightbulb className="h-4 w-4" />
                  {strings.coach_label}
                </p>

                <div className="mt-5 rounded-2xl border border-[#f0d8d5] bg-[#fff8f7] p-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ef5149] text-white">
                      <X className="h-3.5 w-3.5" />
                    </span>
                    <p className="text-base font-semibold leading-6 text-[#172019]">
                      {strings.coach_issue}
                    </p>
                  </div>
                </div>

                <h3 className="mt-6 text-2xl font-semibold leading-tight tracking-[-0.035em] text-[#0d1711]">
                  {strings.coach_heading}
                </h3>

                <div className="mt-5 space-y-3">
                  {[strings.coach_prompt1, strings.coach_prompt2].map(
                    (prompt) => (
                      <div
                        key={prompt}
                        className="flex items-start gap-3 rounded-xl bg-[#eef7e8] p-4"
                      >
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#26734a] text-white">
                          <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                        <p className="text-sm leading-6 text-[#405047]">
                          {prompt}
                        </p>
                      </div>
                    )
                  )}
                </div>

                <p className="mt-auto border-t border-[#e1e6df] pt-4 text-xs leading-5 text-[#69746d]">
                  {strings.coach_takeaway}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
